import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addBidChoiceEvent, fetchLatestBidChoiceEventByReferenceNumberAndEid } from "../../api/bid";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { logout } from "../../util/authentication";
import {
  BID_TYPE,
  BID_UPDATE_TYPE,
  EMPLOYEE_BID_STATUS,
  NC_PTO_FTO_BID_TIME_OFF_TYPES,
} from "../../util/bid/bidConstants";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { getBidName, getDisabledDatesBasedOnSchedule, getNonEmptyChoices } from "../../util/bid/timeOffBidUtils";
import { fetchCurrentEmployeeMonthlyScheduleMap } from "../../api/scheduleEditor.js";
import {
  ADMIN_BID_CHOICE_CHAR_LIMIT,
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMPLOYEE_ID,
  FEATURES,
  HOME_PATH,
  STATION,
} from "../../util/constants.js";
import { convertMonthStringToInteger } from "../../util/dateTimeFormatter.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  PTO_FTO_BID_CHOICE_CONFIRMATION_MESSAGE,
  PTO_FTO_BID_CHOICE_CONFIRMATION_TITLE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import PageLoading from "../PageLoading";
import UnauthorizedMessage from "../UnauthorizedMessage";
import ViewCommentsDialog from "../ViewCommentsDialog";
import BidInfoDialog from "../fundamentals/BidInfoDialog";
import ConfirmButton from "../fundamentals/ConfirmButton";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../fundamentals/SuccessDialog";
import BidChoicesTable from "./BidChoicesTable";
import BidTimer from "./BidTimer";
import { validatePTOCount, validateFTOCount } from "../../util/bid/bidInputValidation";
import PropTypes from "prop-types";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "16px",
    marginTop: "-5px",
  },
  lastUpdated: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  commentBox: {
    width: "80%",
    margin: "10px auto",
  },
  addChoice: {
    margin: "15px",
  },
  bidInput: {
    paddingLeft: "10px",
  },
}));

const ManageNCPTOFTOBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);
  const [ptoCount, setPtoCount] = useState("");
  const [ptoCountErrors, setPtoCountErrors] = useState("");
  const [initialPtoCount, setInitialPtoCount] = useState("");
  const [ftoCount, setFtoCount] = useState("");
  const [ftoCountErrors, setFtoCountErrors] = useState("");
  const [initialFTOCount, setInitialFTOCount] = useState("");

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");
  const [timeOffTypeErrors, setTimeOffTypeErrors] = useState("");

  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);
  const [lastUpdatedComments, setLastUpdatedComments] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");

  // base schedule state
  const [disabledDates, setDisabledDates] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (props.selectedBidGroupEmployeeData) {
        fetchLatestNCPTOFTOBidChoices(props.selectedBidGroupEmployeeData.eid, props.referenceNumber);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState, props.selectedBidGroupEmployeeData]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    if (bidChoiceData) {
      fetchSchedule(bidChoiceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidChoiceData]);

  useEffect(() => {
    // validate choices when any of the counts is present
    if (ptoCount || ftoCount) {
      validatePtoFtoCountWithChoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoices]);

  const handlePtoCountEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handlePtoCountBlur();
    }
  };

  const handlePtoCountChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/[^0-9]/g, "");
    setPtoCount(sanitizedInput);
    setPtoCountErrors("");
  };

  const handlePtoCountBlur = () => {
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfPTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO
    ).length;
    const errors = validatePTOCount(ptoCount, numOfPTOChoices);

    if (errors === "") {
      setPtoCountErrors("");
    } else {
      setPtoCountErrors(errors);
    }
  };

  const handleFTOCountEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleFTOCountBlur();
    }
  };

  const handleFTOCountChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/[^0-9]/g, "");
    setFtoCount(sanitizedInput);
    setFtoCountErrors("");
  };

  const handleFTOCountBlur = () => {
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfFTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO
    ).length;
    const errors = validateFTOCount(ftoCount, numOfFTOChoices);

    if (errors === "") {
      setFtoCountErrors("");
    } else {
      setFtoCountErrors(errors);
    }
  };

  const fetchSchedule = ({ eid, station, schedYear, schedMonth }) => {
    if (eid && station && schedYear && schedMonth) {
      setIsScheduleLoading(true);
      fetchCurrentEmployeeMonthlyScheduleMap(eid, station, schedYear, convertMonthStringToInteger(schedMonth))
        .then((res) => res.json())
        .then((response) => {
          if (response?.event) {
            const disabledDates = getDisabledDatesBasedOnSchedule({
              scheduledMap: response.event,
              station: station,
              bidType: BID_TYPE.NC_PTO_FTO,
              schedMonth: schedMonth,
              schedYear: schedYear,
            });
            setDisabledDates(disabledDates);
            setIsScheduleLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((err) => {
          setIsScheduleLoading(false);
          console.error(`Bid Error: %j`, err);
        });
    }
  };

  const handleCommentsChange = (comments) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(comments.target.value);
  };

  const fetchLatestNCPTOFTOBidChoices = (eid, referenceNumber) => {
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          setBidChoiceData(bidChoiceEvent);
          const choices =
            response.event?.choices?.length > 0 ? response.event.choices : [{ date: "", timeOffType: "" }];
          setSelectedChoices(choices);
          setInitialSelectedChoices(choices);

          const ptoCount = response.event.ptoCount ? response.event.ptoCount : "";
          setPtoCount(ptoCount);
          setInitialPtoCount(ptoCount);

          const ftoCount = response.event.ftoCount ? response.event.ftoCount : "";
          setFtoCount(ftoCount);
          setInitialFTOCount(ftoCount);
          if (response.event?.modifiedByFirstName && response.event?.modifiedByLastName) {
            setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
            setLastUpdatedByLastName(response.event?.modifiedByLastName);
          }
          setIsPageLoading(false);
          setLastUpdatedComments(response.event?.comments);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    //NOSONAR
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(ADMIN_BID_CHOICE_CHAR_LIMIT));
    } else {
      setIsSubmitLoading(true);
      console.log("Saving bid choice data...");
      let payload = buildBidChoiceEvent(
        prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices, ftoCount, ptoCount }),
        userSession,
        comments
      );
      addBidChoiceEvent(payload)
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            console.error("Error: ", result.message);
            setSubmitErrors(result.message);
            setIsSubmitLoading(false);
            setIsSubmitDialogOpen(false);
            setIsSubmitResponseDialogOpen(true);
          } else {
            console.log("Success!");
            props.setIsBidGroupTabChanged(true);
            setIsSubmitDialogOpen(false);
            setIsSubmitLoading(false);
            setIsSubmitResponseDialogOpen(true);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
        });
    }
  };

  const validatePtoFtoCountWithChoices = () => {
    //NOSONAR
    let isFormValid = false;
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfPTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO
    ).length;
    const numOfFTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO
    ).length;

    const ptoErrors = validatePTOCount(ptoCount, numOfPTOChoices);
    const ftoErrors = validateFTOCount(ftoCount, numOfFTOChoices);

    setPtoCountErrors(ptoErrors);
    setFtoCountErrors(ftoErrors);

    if (ptoErrors === "" && ftoErrors === "") {
      isFormValid = true;
    }

    return isFormValid;
  };

  const handleClickSubmitDialogOpen = () => {
    //NOSONAR
    const isValidChoicesWithCounts = validatePtoFtoCountWithChoices();
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    // validate all choices are having timeoff type value or not
    const missingTimeOffTypes = selectedNonEmptyChoices.filter((choice) => choice.timeOffType === "");
    if (missingTimeOffTypes.length > 0) {
      setTimeOffTypeErrors("Time Off Type Required");
    } else {
      setTimeOffTypeErrors("");
    }
    if (isValidChoicesWithCounts && missingTimeOffTypes.length === 0) {
      setSelectedChoices(selectedNonEmptyChoices);
      // open submit dialog when all validations pass
      setIsSubmitDialogOpen(true);
    }
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (
      !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
      !isEqual(ftoCount, initialFTOCount) ||
      !isEqual(ptoCount, initialPtoCount)
    ) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so move away from manage bid choices page
      props.setIsManageNCPTOFTOBidChoicesSelected(false);
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([{ date: "", timeOffType: "" }]);
    setTimeOffTypeErrors("");
    setIsClearAllDialogOpen(false);
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const handleAddChoice = () => {
    const existingChoices = [...selectedChoices];
    setSelectedChoices([...existingChoices, { date: "", timeOffType: "" }]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${selectedChoices.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
    setTimeOffTypeErrors("");
  };

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const renderComments = () => {
    return (
      <>
        <TextField
          className={classes.commentBox}
          value={comments}
          variant="outlined"
          onChange={handleCommentsChange}
          label="Comments (Mandatory)"
          multiline
          error={commentsError}
          helperText={commentErrorMessage}
        />

        {comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT ? (
          <Box sx={{ textAlign: "right", color: "primary.swaRed", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        ) : (
          <Box sx={{ textAlign: "right", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        )}
      </>
    );
  };

  const isSubmitButtonDisabled = () => {
    if (
      bidChoiceData?.updateType === BID_UPDATE_TYPE.DELETE ||
      bidChoiceData?.status === EMPLOYEE_BID_STATUS.WITHDRAWN
    ) {
      return true;
    }

    // No changes have been made from the latest submission
    if (
      isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) &&
      isEqual(ftoCount, initialFTOCount) &&
      isEqual(ptoCount, initialPtoCount)
    ) {
      return true;
    }
  };

  const renderNavBtnsOfBidForm = () => {
    return (
      <>
        {props.isAwardedEmpReadOnlyView ? (
          <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
            <Button
              sx={{
                width: "100px",
              }}
              variant="outlined"
              color="primary"
              onClick={props.handleManagePTOFTOBidBackClick}
            >
              Back
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Box>
            <Box sx={{ paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                disabled={isSubmitButtonDisabled()}
                onClick={handleClickSubmitDialogOpen}
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  const renderSubmitPTOFTOBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 && parseInt(ftoCount) === 0 && parseInt(ptoCount) === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting{" "}
                    <b>
                      {
                        selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO)
                          .length
                      }{" "}
                      choice
                      {selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO)
                        .length > 1 && "s"}
                    </b>
                    {" for "}
                    <b>
                      {ptoCount} PTO{ptoCount > 1 && "s"}
                    </b>
                    {" and "}
                    <b>
                      {
                        selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO)
                          .length
                      }{" "}
                      choice
                      {selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO)
                        .length > 1 && "s"}
                    </b>
                    {" for "}
                    <b>
                      {ftoCount} FTO{ftoCount > 1 && "s"}
                    </b>
                    .
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getLastSubmittedByInfo = () => {
    if (getLastSubmittedTimestamp() !== "") {
      if (
        lastUpdatedByFirstName &&
        lastUpdatedByLastName &&
        lastUpdatedByFirstName !== "" &&
        lastUpdatedByLastName !== ""
      ) {
        return (
          <Grid className={classes.lastUpdated}>
            {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
            {lastUpdatedComments !== "" && (
              <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                (View comment)
              </Link>
            )}
          </Grid>
        );
      } else {
        return <Grid className={classes.lastUpdated}>{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>;
      }
    }
    return "";
  };
  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    `${BREADCRUMBS_TITLE.MANAGE_NC_PTO_FTO_BID} / ${props.selectedBidGroupEmployeeData.eid}`,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];

  const renderPTOFTOBidChoicesPage = () => {
    if (isPageLoading || isScheduleLoading) {
      return <PageLoading />;
    } else {
      return (
        <>
          <StarsBreadcrumbs
            breadcrumbsList={breadcrumbsList}
            breadcrumbsLinks={breadcrumbsLinks}
            isConfirmationRequired={
              !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
              !isEqual(ftoCount, initialFTOCount) ||
              !isEqual(ptoCount, initialPtoCount)
            }
            cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
          />
          {/* Title & Cancel/Submit Buttons */}
          <Grid container>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
                <Grid item>
                  <Typography variant="h5" component="div" align="left">
                    {getBidName(bidChoiceData)} PTO/FTO Bid
                  </Typography>
                  {getLastSubmittedByInfo()}

                  <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                    {bidChoiceData &&
                      bidChoiceData.windowStartDate !== null &&
                      bidChoiceData.windowEndDate !== null && (
                        <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                          <BidTimer
                            windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                            station={bidChoiceData.station}
                          />
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>

              <Stack>
                <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                  {renderNavBtnsOfBidForm()}
                </Grid>
              </Stack>
            </Grid>
            {props.selectedBidGroupEmployeeData && (
              <Grid
                container
                sx={{
                  pt: 3,
                  paddingLeft: "2rem",
                }}
                spacing={2}
              >
                <Grid item md={4}>
                  <div style={{ display: "flex" }}>
                    <div>
                      <TextField
                        style={{ maxWidth: "17vw", margin: "auto", textAlign: "left", width: "90px" }}
                        label={STATION}
                        variant="outlined"
                        value={props.station}
                        disabled
                      />
                    </div>
                    <div style={{ paddingLeft: "10px" }}>
                      <TextField
                        className={classes.inputText}
                        label={EMPLOYEE_ID}
                        variant="outlined"
                        value={props.selectedBidGroupEmployeeData?.eid}
                        disabled
                      />
                    </div>
                    <div>
                      <Button style={{ padding: "15px" }} variant="contained" color="primary" disabled>
                        View
                      </Button>
                    </div>
                  </div>
                </Grid>
                <Grid
                  item
                  md={8}
                  sx={{
                    paddingRight: "3.2rem",
                  }}
                >
                  {props.selectedBidGroupEmployeeData ? (
                    <div>
                      <Grid
                        container
                        sx={{
                          backgroundColor: "secondary.swaGray1",
                          height: "auto",
                          borderRadius: "5px",
                          marginLeft: "20px",
                        }}
                      >
                        <Grid
                          item
                          xs={4}
                          style={{ fontSize: "24px", paddingTop: "11px", paddingLeft: "16px", textAlign: "left" }}
                        >
                          {props.selectedBidGroupEmployeeData.lastName +
                            ", " +
                            props.selectedBidGroupEmployeeData.shortName}
                        </Grid>
                        <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                          <Stack direction="row">
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                HOME STATION
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {props.selectedBidGroupEmployeeData.homeStation}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                WORKGROUP
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {getWorkgroupGivenJobCode(props.selectedBidGroupEmployeeData.homeAccountJobCode)}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                STATUS
                              </Typography>
                              <p className={classes.employeeDataValue}>{props.selectedBidGroupEmployeeData.ftpt}</p>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </div>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            )}
            <Box
              sx={{
                width: "100%",
                marginLeft: "2rem",
                marginRight: "2rem",
                marginTop: "1rem",
                border: "solid 1px",
                borderColor: "secondary.swaGray2",
              }}
            >
              <>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "15px",
                    }}
                  >
                    Enter the # of PTOs you would like to use (0-31):
                  </Grid>
                  <Grid item>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ptoCount}
                        onChange={handlePtoCountChange}
                        onBlur={handlePtoCountBlur}
                        onKeyUp={handlePtoCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ptoCountErrors !== ""}
                        helperText={ptoCountErrors}
                        disabled={props.isAwardedEmpReadOnlyView}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "15px",
                    }}
                  >
                    Enter the # of FTOs you would like to use (0-10):
                  </Grid>
                  <Grid item>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ftoCount}
                        onChange={handleFTOCountChange}
                        onBlur={handleFTOCountBlur}
                        onKeyUp={handleFTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ftoCountErrors !== ""}
                        helperText={ftoCountErrors}
                        disabled={props.isAwardedEmpReadOnlyView}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                    marginTop: "10px",
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    {props.isAwardedEmpReadOnlyView ? (
                      <Typography sx={{ fontWeight: "bold" }}>
                        Review your {getNonEmptyChoices(selectedChoices).length} choice
                        {getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontWeight: "bold" }}>
                        {getNonEmptyChoices(selectedChoices).length}{" "}
                        {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                      disabled={props.isAwardedEmpReadOnlyView}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  sx={{
                    borderBottom: 0,
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    {
                      <BidChoicesTable
                        selectedChoices={selectedChoices}
                        setSelectedChoices={setSelectedChoices}
                        isDesktopView={true}
                        isAdmin={true}
                        isBidChoicesLoading={isPageLoading}
                        bidChoiceData={bidChoiceData}
                        disabledDates={disabledDates}
                        isReadOnly={props.isAwardedEmpReadOnlyView}
                        selectedAwardedBid={props.selectedAwardedBid}
                        bidType={bidChoiceData.bidType}
                        timeOffTypeErrors={timeOffTypeErrors}
                      />
                    }
                    <Divider />
                    <Button
                      className={classes.addChoice}
                      variant="contained"
                      onClick={handleAddChoice}
                      disabled={
                        !!selectedChoices.some((choice) => choice.date === "" || choice.timeOffType === "") ||
                        props.isAwardedEmpReadOnlyView
                      }
                    >
                      Add Another Choice
                    </Button>
                  </Box>
                </Grid>
              </>
            </Box>

            {/* Dialogs */}
            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title="Cancel"
              message={BID_FORM_CANCEL_MESSAGE}
              handleNoButtonClicked={handleClickCancelDialogClose}
              handleYesButtonClicked={() => props.setIsManageNCPTOFTOBidChoicesSelected(false)}
            />
            {renderSubmitPTOFTOBidChoiceDialog()}
            {viewCommentsDialogOpen && (
              <ViewCommentsDialog
                isDialogOpen={viewCommentsDialogOpen}
                handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
                header={`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} on `}
                publishedTimestamp={getLastUpdatedTimeString()}
                message={lastUpdatedComments}
              />
            )}
            <BidInfoDialog
              isDialogOpen={isGuideDialogOpen}
              title="Guide"
              handleCloseClicked={handleCloseClickedGuideDialog}
              isDesktopView={props.isDesktopView}
              eid={userSession.eid}
              bidChoiceData={bidChoiceData}
              lastSubmittedTimestamp={getLastSubmittedTimestamp()}
              bidType={bidChoiceData.bidType}
            />
            <SuccessDialog
              isSuccessDialogOpen={isSubmitResponseDialogOpen}
              title={PTO_FTO_BID_CHOICE_CONFIRMATION_TITLE}
              message={PTO_FTO_BID_CHOICE_CONFIRMATION_MESSAGE}
              hasError={submitErrors !== ""}
              error={BID_CHOICE_SAVE_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsSubmitResponseDialogOpen(false);
                props.setIsManageNCPTOFTOBidChoicesSelected(false);
              }}
            />
          </Grid>
        </>
      );
    }
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents enable-scroll" data-testid="manage-pto-fto-bid-choices-page">
      {isUserAuthorizedStateLoading ? <PageLoading /> : renderPTOFTOBidChoicesPage()}
    </div>
  );
};

ManageNCPTOFTOBidChoices.propTypes = {
  selectedBidGroupEmployeeData: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  referenceNumber: PropTypes.string.isRequired,
  featureConfig: PropTypes.object.isRequired,
  setIsManageNCPTOFTOBidChoicesSelected: PropTypes.func.isRequired,
  handleManagePTOFTOBidBackClick: PropTypes.func.isRequired,
  isAwardedEmpReadOnlyView: PropTypes.bool,
  selectedAwardedBid: PropTypes.object,
  setIsBidGroupTabChanged: PropTypes.func.isRequired,
};

export default ManageNCPTOFTOBidChoices;
